"use client";

import { useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { authClient } from "@/lib/auth-client";
import { User, AtSign, Mail, Lock, Eye, EyeOff } from "lucide-react";
import { Modal } from "@/components/ui";

const CGU_CONTENT = (
  <div className="space-y-6 text-sm text-slate-600">
    <div className="text-center border-b border-slate-200 pb-4">
      <p className="font-bold text-slate-900">Coraia</p>
      <p className="font-semibold text-slate-800 mt-2">Contrat de conditions d&apos;utilisations de la phase de test de l&apos;application Axia</p>
      <p className="text-xs text-slate-500 mt-1">Rédigé le 06/02/2026 par Ernest Naveos, Co-fondateur de Coraia Agency</p>
    </div>

    <p>Coraia, développeur et éditeur de l&apos;application Axia, et toute personne physique ou morale accédant au logiciel Axia dans le cadre de la phase de test (ci-après dénommée « l&apos;utilisateur testeur »).</p>

    <section>
      <h4 className="font-semibold text-slate-900">Objet du contrat</h4>
      <p>Le contrat ici présent a pour objet de définir les conditions auxquelles seront soumis les utilisateurs testeurs qui accèdent à l&apos;application Axia dans le cadre d&apos;une phase de test encadrée définie par Coraia et destinée au développement interne de l&apos;application.</p>
      <p className="mt-2">Les objectifs de cette phase de test sont :</p>
      <ul className="list-disc pl-5 mt-1 space-y-1">
        <li>D&apos;évaluer l&apos;efficacité et la fiabilité de l&apos;outil de rédaction de l&apos;application</li>
        <li>D&apos;obtenir des retours des utilisateurs testeurs en vue d&apos;optimiser l&apos;interface</li>
        <li>D&apos;obtenir des retours utilisateurs en vue d&apos;identifier des dysfonctionnements</li>
      </ul>
    </section>

    <section>
      <h4 className="font-semibold text-slate-900">Conditions d&apos;accès</h4>
      <p>L&apos;accès à la phase de test est accordé en contrepartie :</p>
      <ul className="list-disc pl-5 mt-1 space-y-1">
        <li>D&apos;un paiement unique d&apos;un montant de 24€, non remboursable</li>
        <li>De l&apos;engagement de l&apos;utilisateur testeur à fournir à Coraia des retours constructifs sur l&apos;utilisation du logiciel</li>
      </ul>
    </section>

    <section>
      <h4 className="font-semibold text-slate-900">Engagements de l&apos;utilisateur testeur</h4>
      <p>En acceptant le présent contrat, l&apos;utilisateur testeur s&apos;engage à :</p>
      <ul className="list-disc pl-5 mt-1 space-y-1">
        <li>Utiliser l&apos;application Axia de manière conforme à sa destination, dans le cadre de la pratique professionnelle de l&apos;orthoptie</li>
        <li>Signaler tout bugs, problèmes techniques ou défauts majeurs observés</li>
        <li>Fournir des retours exploitables portant sur l&apos;utilisation de l&apos;application</li>
        <li>Être l&apos;unique utilisateur du compte fourni par Coraia donnant accès à la phase de test</li>
        <li>Ne pas utiliser Axia à des fins illégales, frauduleuses ou détournées des fonctionnalités initiales de l&apos;application</li>
      </ul>
      <p className="mt-2">Coraia se réserve le droit de retirer l&apos;accès à tout utilisateur testeur ne respectant pas ces engagements, sans indemnité ni obligations quelconques.</p>
    </section>

    <section>
      <h4 className="font-semibold text-slate-900">Contrepartie accordée à l&apos;utilisateur testeur</h4>
      <p>En contrepartie de sa participation active à la phase de test, l&apos;utilisateur testeur bénéficiera :</p>
      <ul className="list-disc pl-5 mt-1 space-y-1">
        <li>D&apos;un accès anticipé à l&apos;application Axia</li>
        <li>De deux (2) mois d&apos;abonnement offerts lors de l&apos;ouverture officielle de l&apos;application au public</li>
      </ul>
      <p className="mt-2">Ces deux mois gratuits seront automatiquement appliqués au compte de l&apos;utilisateur testeur lors du lancement public, sous réserve que le compte soit toujours actif.</p>
    </section>

    <section>
      <h4 className="font-semibold text-slate-900">Propriété intellectuelle</h4>
      <p>Axia, ainsi que l&apos;ensemble de ses éléments (code, interface, fonctionnalités, contenus, marques) demeure la propriété exclusive de Coraia.</p>
      <p className="mt-2">Le présent contrat n&apos;emporte aucune cession de droits de propriété intellectuelle au profit de l&apos;utilisateur testeur.</p>
      <p className="mt-2">Les retours, suggestions et idées fournis par l&apos;utilisateur testeur pourront être librement utilisés par Coraia, sans contrepartie financière ou intellectuelle supplémentaire.</p>
      <p className="mt-2">L&apos;utilisateur testeur s&apos;engage à ne pas divulguer, directement ou indirectement, ces informations à des tiers, et notamment à tout concurrent potentiel ou avéré de Coraia, pendant la durée de la phase de test et après sa fin.</p>
    </section>

    <section>
      <h4 className="font-semibold text-slate-900">Durée et fin de la phase de test</h4>
      <p>La phase de test débute à la date d&apos;acceptation du présent contrat et prendra fin à la date décidée par Coraia.</p>
      <p className="mt-2">Coraia se réserve le droit de mettre fin à la phase de test à tout moment et de modifier les conditions de la phase de test si nécessaire.</p>
    </section>

    <section className="border-t border-slate-200 pt-4">
      <h4 className="font-semibold text-slate-900">Acceptation du contrat</h4>
      <p>L&apos;accès à Axia dans le cadre de la phase de test vaut acceptation pleine et entière du présent contrat par l&apos;utilisateur testeur.</p>
    </section>
  </div>
);

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden>
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  );
}

export default function RegisterPage() {
  const router = useRouter();
  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingGoogle, setLoadingGoogle] = useState(false);
  const [acceptCGU, setAcceptCGU] = useState(false);
  const [showCGU, setShowCGU] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (password.length < 8) {
      setError("Le mot de passe doit contenir au moins 8 caractères");
      return;
    }

    if (!acceptCGU) {
      setError("Vous devez accepter les conditions générales d'utilisation");
      return;
    }

    const cleanUsername = username.trim().replace(/^@+/, "");
    if (cleanUsername.length > 0 && cleanUsername.length < 3) {
      setError("Le nom d'utilisateur doit contenir au moins 3 caractères (sans le @)");
      return;
    }
    if (cleanUsername.length > 32) {
      setError("Le nom d'utilisateur ne peut pas dépasser 32 caractères");
      return;
    }

    setLoading(true);

    try {
      const result = await authClient.signUp.email({
        email,
        password,
        name,
        ...(cleanUsername ? { username: cleanUsername } : {}),
      });

      if (result.error) {
        setError(result.error.message || "Échec de la création du compte. Vérifiez que l'email ou le nom d'utilisateur n'est pas déjà utilisé.");
      } else {
        router.push("/dashboard");
        router.refresh();
      }
    } catch (err) {
      setError("Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError("");
    setLoadingGoogle(true);
    try {
      await authClient.signIn.social({
        provider: "google",
        callbackURL: "/dashboard",
      });
    } catch {
      setError("Inscription avec Google impossible. Vérifiez la configuration.");
    } finally {
      setLoadingGoogle(false);
    }
  };

  return (
    <div className="card p-8">
      <div className="text-center mb-8">
        <div className="flex justify-center mb-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary-600 text-white font-bold text-xl">
            A
          </div>
        </div>
        <h1 className="text-2xl font-bold text-slate-900">Créer un compte</h1>
        <p className="mt-2 text-sm text-slate-500">Rejoignez AXIA gratuitement</p>
      </div>

      {error && (
        <div className="mb-6 rounded-lg bg-error-50 border border-error-200 p-4 text-sm text-error-700">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-1.5">
            Nom complet
          </label>
          <div className="relative">
            <User className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <input
              id="name"
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input pl-11"
              placeholder="Jean Dupont"
              disabled={loading}
            />
          </div>
        </div>

        <div>
          <label htmlFor="username" className="block text-sm font-medium text-slate-700 mb-1.5">
            Nom d&apos;utilisateur
          </label>
          <div className="relative">
            <AtSign className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <input
              id="username"
              type="text"
              autoComplete="username"
              minLength={3}
              maxLength={32}
              value={username}
              onChange={(e) => setUsername(e.target.value.replace(/\s/g, "").replace(/^@+/, ""))}
              className="input pl-11"
              placeholder="lucca (sans le @, min. 3 caractères)"
              disabled={loading}
            />
          </div>
          <p className="mt-1 text-xs text-slate-500">Optionnel. Permet de se connecter avec ce nom au lieu de l&apos;email.</p>
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1.5">
            Email
          </label>
          <div className="relative">
            <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input pl-11"
              placeholder="vous@exemple.com"
              disabled={loading}
            />
          </div>
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1.5">
            Mot de passe
          </label>
          <div className="relative">
            <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input pl-11 pr-11"
              placeholder="Min. 8 caractères"
              disabled={loading}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
        </div>

        <div className="flex items-start gap-3">
          <div className="flex h-5 items-center">
            <input
              id="cgu"
              name="cgu"
              type="checkbox"
              required
              checked={acceptCGU}
              onChange={(e) => setAcceptCGU(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-600"
            />
          </div>
          <div className="text-sm">
            <label htmlFor="cgu" className="font-medium text-slate-700">
              J&apos;accepte les{" "}
              <button
                type="button"
                className="text-primary-600 hover:underline"
                onClick={() => setShowCGU(true)}
              >
                conditions générales d&apos;utilisation
              </button>
            </label>
            <p className="text-slate-500">
              En cochant cette case, vous acceptez nos CGU et confirmez avoir lu notre politique de confidentialité.
            </p>
          </div>
        </div>

        <button type="submit" className="btn-primary w-full" disabled={loading}>
          {loading ? "Création..." : "Créer mon compte"}
        </button>

        <div className="relative my-4">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-slate-200" />
          </div>
          <div className="relative flex justify-center text-xs">
            <span className="bg-white px-2 text-slate-500">ou</span>
          </div>
        </div>

        <button
          type="button"
          onClick={handleGoogleSignIn}
          disabled={loadingGoogle}
          className="flex w-full items-center justify-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50 disabled:opacity-50"
        >
          <GoogleIcon className="h-5 w-5" />
          {loadingGoogle ? "Inscription..." : "S'inscrire avec Google"}
        </button>
      </form>

      <p className="mt-6 text-center text-sm text-slate-500">
        Déjà un compte ?{" "}
        <Link href="/login" className="font-semibold text-primary-600 hover:text-primary-700">
          Se connecter
        </Link>
      </p>
      <Modal
        isOpen={showCGU}
        onClose={() => setShowCGU(false)}
        title="Conditions Générales d'Utilisation"
      >
        {CGU_CONTENT}
      </Modal>
    </div>
  );
}
